import axios, { InternalAxiosRequestConfig } from 'axios';
import { API_TIMEOUT, EnvType } from '../../APIConstants';
import {
  getAGraphBaseUrl,
  insertApiEnvironmentIntoBaseUrl,
  ResponseTransformConfig,
  setAGraphHeaders,
} from '../../utils';
import { applyAGraphResponseTransformations } from '../../utils/ApiUtils';
import { AGraphApiSettings } from '../Base';
import {
  AGRAPH_AGGREGATE_POST_ENUM_FIELDS,
  AGRAPH_AGGREGATE_POST_TIMESTAMP_FIELDS,
  AGRAPH_PORTFOLIO_ENUM_PREFIXES,
  AGRAPH_PORTFOLIO_POST_TIMESTAMP_FIELDS,
} from './AGraphConstants';
import { conditionalParam, isAGraphEnabled } from './AGraphUtils';
import {
  ACTAS_USER_OVERRIDE_APPLICABLE_ENDPOINTS,
  AW_PORTFOLIO_API_URL,
  AW_PORTFOLIO_API_URL_AGRAPH,
} from './Constants';

export const awPortfolioSettings: AGraphApiSettings & {
  getPkgFn?: () => string | undefined;
  getViewAsLogin?: () => string | undefined;
  getEnvTypeFn?: () => EnvType;
  getActAsLoginFn?: () => string | undefined;
} = {
  getPkgFn: undefined,
};

export const awPortfolioApi = axios.create({
  timeout: API_TIMEOUT,
});

awPortfolioApi.interceptors.request.use((config) => {
  const { getAladdinApiKey, getConfigurationDirectoryFn, getPkgFn, getViewAsLogin, getEnvTypeFn, getActAsLoginFn } =
    awPortfolioSettings;
  const configurationDirectory = getConfigurationDirectoryFn?.();
  const viewAs = getViewAsLogin?.();
  const envType = getEnvTypeFn?.();
  const actAs = getActAsLoginFn?.();
  const baseURL = isAGraphEnabled()
    ? getAGraphBaseUrl(AW_PORTFOLIO_API_URL_AGRAPH)
    : insertApiEnvironmentIntoBaseUrl(AW_PORTFOLIO_API_URL, '/api', envType, '-', true);

  if (!getPkgFn) {
    throw Error('You must define a function for awPortfolioSettings.getPkgFn before using this API');
  }

  const params: Record<string, unknown> = {
    ...config.params,
    pkg: config.params?.pkg ?? getPkgFn(),
    ...(configurationDirectory && {
      configurationDirectory: conditionalParam(config.url, 'configurationDirectory', configurationDirectory),
    }),
    viewAs: conditionalParam(config.url, 'viewAs', viewAs),
  };

  // Add userOverride for ActAs mode on specific endpoints
  if (actAs && ACTAS_USER_OVERRIDE_APPLICABLE_ENDPOINTS.some((endpoint) => config.url?.includes(endpoint))) {
    params.userOverride = actAs;
  }

  const updatedConfig: InternalAxiosRequestConfig = {
    ...config,
    baseURL,
    params,
  };
  setAGraphHeaders(updatedConfig, getAladdinApiKey?.(), envType);

  return updatedConfig;
});

// Apply response transformations for AGraph API
awPortfolioApi.interceptors.response.use((response) => {
  if (isAGraphEnabled() && response.data) {
    const AGRAPH_RESPONSE_TRANSFORMS: ResponseTransformConfig[] = [
      {
        urlPattern: '/aggregates/{lookup-from-accounts,user-paginate}',
        method: 'GET',
        timestampFields: ['asOf', 'creationTimestamp', 'lastUpdatedTimestamp', 'createTime', 'updateTime'],
        enumPrefixesToRemove: [
          {
            fieldNames: ['valueType'],
            prefix: 'VALUE_TYPE_',
            recursiveFieldPatterns: [
              'items',
              'mandateRestrictions',
              'benchmarkPortfolio',
              'modelPortfolio',
              'otherBenchmarks',
              'benchmark',
              'otherModels',
              'model',
              'resourceValues',
              'portfolios',
              'childAggregateValues',
            ],
          },
          {
            fieldNames: ['origin'],
            prefix: 'ORIGIN_',
            recursiveFieldPatterns: [
              'items',
              'benchmarkPortfolio',
              'modelPortfolio',
              'otherBenchmarks',
              'benchmark',
              'otherModels',
              'model',
              'resourceValues',
              'portfolios',
              'childAggregateValues',
              'benchmarkRef',
              'modelRef',
              'benchRef',
              'supplement',
              'sleeveInfo',
              'modelRefs',
              'resourceEntities',
              'portfolioEntityRefs',
              'childAggregateEntities',
            ],
          },
          {
            fieldNames: ['application'],
            prefix: 'APPLICATION_',
            recursiveFieldPatterns: [
              'items',
              'mandateRestrictions',
              'benchmarkPortfolio',
              'modelPortfolio',
              'otherBenchmarks',
              'benchmark',
              'otherModels',
              'model',
              'resourceValues',
              'portfolios',
              'childAggregateValues',
            ],
          },
          {
            fieldNames: ['type'],
            prefix: 'RESTRICTION_TYPE_',
            recursiveFieldPatterns: [
              'items',
              'mandateRestrictions',
              'benchmarkPortfolio',
              'modelPortfolio',
              'otherBenchmarks',
              'benchmark',
              'otherModels',
              'model',
              'resourceValues',
              'portfolios',
              'childAggregateValues',
            ],
          },
          {
            fieldNames: ['severity'],
            prefix: 'SEVERITY_',
            recursiveFieldPatterns: [
              'items',
              'mandateRestrictions',
              'benchmarkPortfolio',
              'modelPortfolio',
              'otherBenchmarks',
              'benchmark',
              'otherModels',
              'model',
              'resourceValues',
              'portfolios',
              'childAggregateValues',
            ],
          },
        ],
        flattenPortfolioEntityRefs: ['resourceEntities'],
      },
      {
        urlPattern: '/aggregates/*/*/_entitlement-status',
        method: 'GET',
        timestampFields: ['asOf'],
        enumPrefixesToRemove: [
          {
            fieldNames: ['origin'],
            prefix: 'ORIGIN_',
            recursiveFieldPatterns: ['entitlementStatuses', 'accountRef'],
          },
        ],
      },
      {
        urlPattern: '/aggregates/search',
        method: 'POST',
        timestampFields: ['asOf', 'creationTimestamp', 'lastUpdatedTimestamp', 'createTime', 'updateTime'],
        enumPrefixesToRemove: [
          {
            fieldNames: ['valueType'],
            prefix: 'VALUE_TYPE_',
            recursiveFieldPatterns: [
              'result',
              'items',
              'mandateRestrictions',
              'benchmarkPortfolio',
              'modelPortfolio',
              'otherBenchmarks',
              'benchmark',
              'otherModels',
              'model',
              'resourceValues',
              'portfolios',
              'childAggregateValues',
            ],
          },
          {
            fieldNames: ['origin'],
            prefix: 'ORIGIN_',
            recursiveFieldPatterns: [
              'result',
              'items',
              'benchmarkPortfolio',
              'modelPortfolio',
              'otherBenchmarks',
              'benchmark',
              'otherModels',
              'model',
              'resourceValues',
              'portfolios',
              'childAggregateValues',
              'benchmarkRef',
              'modelRef',
              'benchRef',
              'supplement',
              'sleeveInfo',
              'modelRefs',
              'resourceEntities',
              'portfolioEntityRefs',
              'childAggregateEntities',
            ],
          },
          {
            fieldNames: ['application'],
            prefix: 'APPLICATION_',
            recursiveFieldPatterns: [
              'result',
              'items',
              'mandateRestrictions',
              'benchmarkPortfolio',
              'modelPortfolio',
              'otherBenchmarks',
              'benchmark',
              'otherModels',
              'model',
              'resourceValues',
              'portfolios',
              'childAggregateValues',
            ],
          },
          {
            fieldNames: ['type'],
            prefix: 'RESTRICTION_TYPE_',
            recursiveFieldPatterns: [
              'result',
              'items',
              'mandateRestrictions',
              'benchmarkPortfolio',
              'modelPortfolio',
              'otherBenchmarks',
              'benchmark',
              'otherModels',
              'model',
              'resourceValues',
              'portfolios',
              'childAggregateValues',
            ],
          },
          {
            fieldNames: ['severity'],
            prefix: 'SEVERITY_',
            recursiveFieldPatterns: [
              'result',
              'items',
              'mandateRestrictions',
              'benchmarkPortfolio',
              'modelPortfolio',
              'otherBenchmarks',
              'benchmark',
              'otherModels',
              'model',
              'resourceValues',
              'portfolios',
              'childAggregateValues',
            ],
          },
        ],
      },
      {
        urlPattern: '/aggregates',
        method: 'POST',
        timestampFields: AGRAPH_AGGREGATE_POST_TIMESTAMP_FIELDS,
        enumPrefixesToRemove: AGRAPH_AGGREGATE_POST_ENUM_FIELDS,
        flattenPortfolioEntityRefs: ['resourceEntities'],
      },
      {
        urlPattern: '/portfolios/{static-benchmarks,static-models,user-paginate/*,search}',
        timestampFields: ['createTime', 'updateTime', 'asOf', 'creationTimestamp', 'lastUpdatedTimestamp'],
        enumPrefixesToRemove: [
          {
            fieldNames: ['valueType'],
            prefix: 'VALUE_TYPE_',
            recursiveFieldPatterns: ['mandateRestrictions'],
          },
          {
            fieldNames: ['origin'],
            prefix: 'ORIGIN_',
            recursiveFieldPatterns: [
              'otherBenchmarks',
              'benchRef',
              'otherModels',
              'modelRef',
              'supplement',
              'sleeveInfo',
              'modelRefs',
              'benchmarkRef',
              'benchmarkPortfolio',
            ],
          },
          {
            fieldNames: ['application'],
            prefix: 'APPLICATION_',
            recursiveFieldPatterns: ['mandateRestrictions'],
          },
          {
            fieldNames: ['type'],
            prefix: 'RESTRICTION_TYPE_',
            recursiveFieldPatterns: ['mandateRestrictions'],
          },
          {
            fieldNames: ['severity'],
            prefix: 'SEVERITY_',
            recursiveFieldPatterns: ['mandateRestrictions'],
          },
        ],
        flattenOpenLots: { recursiveFieldPatterns: ['items', 'portfolios'] },
        renameFields: [
          { from: 'benchmarkPortfolio', to: 'benchmark' },
          { from: 'modelPortfolio', to: 'model' },
        ],
      },
      {
        urlPattern: '/portfolios/static-models',
        method: 'GET',
        enumPrefixesToRemove: [
          {
            fieldNames: ['valueType'],
            prefix: 'VALUE_TYPE_',
            recursiveFieldPatterns: ['portfolios', 'mandateRestrictions'],
          },
          {
            fieldNames: ['origin'],
            prefix: 'ORIGIN_',
            recursiveFieldPatterns: [
              'portfolios',
              'otherBenchmarks',
              'benchRef',
              'otherModels',
              'modelRef',
              'supplement',
              'sleeveInfo',
              'modelRefs',
              'benchmarkRef',
            ],
          },
          {
            fieldNames: ['application'],
            prefix: 'APPLICATION_',
            recursiveFieldPatterns: ['portfolios', 'mandateRestrictions'],
          },
          {
            fieldNames: ['type'],
            prefix: 'RESTRICTION_TYPE_',
            recursiveFieldPatterns: ['portfolios', 'mandateRestrictions'],
          },
          {
            fieldNames: ['severity'],
            prefix: 'SEVERITY_',
            recursiveFieldPatterns: ['portfolios', 'mandateRestrictions'],
          },
        ],
        timestampFields: ['createTime', 'updateTime', 'asOf', 'creationTimestamp', 'lastUpdatedTimestamp'],
      },
      {
        urlPattern: '/portfolios{,/*/*}',
        timestampFields: AGRAPH_PORTFOLIO_POST_TIMESTAMP_FIELDS,
        enumPrefixesToRemove: AGRAPH_PORTFOLIO_ENUM_PREFIXES,
        flattenOpenLots: true,
        renameFields: [
          { from: 'benchmarkPortfolio', to: 'benchmark' },
          { from: 'modelPortfolio', to: 'model' },
        ],
      },
      {
        urlPattern: '/portfolios/*/*/linked-entities',
        method: 'GET',
        timestampFields: ['lastUpdatedTimestamp', 'asOf'],
        enumPrefixesToRemove: [
          {
            fieldNames: ['origin'],
            prefix: 'ORIGIN_',
            recursiveFieldPatterns: ['linkedEntities', 'entity'],
          },
        ],
      },
      {
        urlPattern: '/portfolios{:bulkCreate,/bulkGet}',
        method: 'POST',
        timestampFields: AGRAPH_PORTFOLIO_POST_TIMESTAMP_FIELDS,
        enumPrefixesToRemove: AGRAPH_PORTFOLIO_ENUM_PREFIXES,
        flattenOpenLots: { recursiveFieldPatterns: ['results'] },
        renameFields: [
          { from: 'benchmarkPortfolio', to: 'benchmark' },
          { from: 'modelPortfolio', to: 'model' },
        ],
      },
      {
        urlPattern: '/sleeve-info/bulkGet',
        method: 'POST',
        timestampFields: ['createTime', 'updateTime', 'asOf', 'creationTimestamp', 'lastUpdatedTimestamp'],
        enumPrefixesToRemove: [
          {
            fieldNames: ['valueType'],
            prefix: 'VALUE_TYPE_',
            recursiveFieldPatterns: ['items', 'modelRefs', 'model', 'mandateRestrictions', 'benchmark', 'models'],
          },
          {
            fieldNames: ['origin'],
            prefix: 'ORIGIN_',
            recursiveFieldPatterns: [
              'items',
              'modelRefs',
              'model',
              'benchmark',
              'models',
              'otherBenchmarks',
              'benchRef',
              'otherModels',
              'modelRef',
              'benchmarkRef',
            ],
          },
          {
            fieldNames: ['application'],
            prefix: 'APPLICATION_',
            recursiveFieldPatterns: ['items', 'modelRefs', 'model', 'mandateRestrictions', 'benchmark', 'models'],
          },
          {
            fieldNames: ['type'],
            prefix: 'RESTRICTION_TYPE_',
            recursiveFieldPatterns: ['items', 'modelRefs', 'model', 'mandateRestrictions', 'benchmark', 'models'],
          },
          {
            fieldNames: ['severity'],
            prefix: 'SEVERITY_',
            recursiveFieldPatterns: ['items', 'modelRefs', 'model', 'mandateRestrictions', 'benchmark', 'models'],
          },
        ],
        flattenOpenLots: { recursiveFieldPatterns: ['items', 'benchmark'] },
      },
    ];
    applyAGraphResponseTransformations(response, AGRAPH_RESPONSE_TRANSFORMS);
  }

  return response;
});
